import { ApiResult } from '@/lib/api/index';

const BACKEND_ENABLED = process.env.NEXT_PUBLIC_BACKEND_ENABLED === 'true';

export interface ServiceRequestPayload {
  serviceType: 'shop' | 'onsite';
  device: {
    category: string;
    brand: string;
    model: string;
  };
  problem: {
    issueType: string;
    description: string;
    photos?: File[];
  };
  location?: {
    address: string;
    apt?: string;
    city: string;
    state: string;
    zip: string;
  };
  preferredTime?: {
    date: string;
    window: string;
  };
  contact: {
    name: string;
    phone: string;
    email: string;
    preferredContact: 'call' | 'whatsapp';
    consent: boolean;
  };
  // Idempotency key prevents duplicate submissions on retry/refresh
  idempotencyKey?: string;
}

export interface ContactMessagePayload {
  name: string;
  phone: string;
  email: string;
  message: string;
}

export interface ApiResult<T = unknown> {
  success: boolean;
  data?: T;
  error?: string;
  notConnected?: boolean;
}

function getUtmData() {
  if (typeof window === 'undefined') return {};
  try {
    const stored = sessionStorage.getItem('fts_utm');
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
}

export function captureUtmParams() {
  if (typeof window === 'undefined') return;
  try {
    const params = new URLSearchParams(window.location.search);
    const utm: Record<string, string> = {};
    ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term'].forEach((key) => {
      const val = params.get(key);
      if (val) utm[key] = val;
    });
    if (Object.keys(utm).length > 0) {
      utm.referrer = document.referrer;
      utm.landingPage = window.location.pathname;
      sessionStorage.setItem('fts_utm', JSON.stringify(utm));
    }
  } catch {}
}

export async function submitServiceRequest(
  payload: ServiceRequestPayload,
  formStartedAt?: number
): Promise<ApiResult<{ referenceNumber: string }>> {
  if (!BACKEND_ENABLED) {
    return {
      success: false,
      notConnected: true,
      error: 'Online submission is not yet connected. Please call or WhatsApp us to arrange your repair.',
    };
  }

  try {
    // Upload photos first
    let photoStoragePaths: string[] = [];
    if (payload.problem.photos?.length) {
      const formData = new FormData();
      payload.problem.photos.forEach((file) => formData.append('files', file));
      const uploadRes = await fetch('/api/upload', { method: 'POST', body: formData });
      const uploadJson = await uploadRes.json();
      if (uploadJson.ok) {
        photoStoragePaths = uploadJson.data.paths;
      }
    }

    const utm = getUtmData();

    const body = {
      serviceType: payload.serviceType === 'shop' ? 'shop_visit' : 'on_site',
      deviceCategory: payload.device.category,
      brandModel: [payload.device.brand, payload.device.model].filter(Boolean).join(' '),
      issueType: payload.problem.issueType,
      problemDescription: payload.problem.description,
      addressLine: payload.location?.address,
      addressLine2: payload.location?.apt,
      city: payload.location?.city,
      state: payload.location?.state,
      zip: payload.location?.zip,
      preferredDate: payload.preferredTime?.date,
      preferredTimeWindow: payload.preferredTime?.window,
      customerName: payload.contact.name,
      customerPhone: payload.contact.phone,
      customerEmail: payload.contact.email,
      preferredContactMethod: payload.contact.preferredContact,
      consentContact: payload.contact.consent,
      source: 'form',
      utmSource: utm.utm_source,
      utmMedium: utm.utm_medium,
      utmCampaign: utm.utm_campaign,
      utmTerm: utm.utm_term,
      referrer: utm.referrer,
      landingPage: utm.landingPage,
      photoStoragePaths,
      formStartedAt,
      idempotencyKey: payload.idempotencyKey,
    };

    const res = await fetch('/api/service-request', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });

    const json = await res.json();

    if (json.ok) {
      trackEvent('form_submit', {
        service_type: payload.serviceType,
        device_category: payload.device.category,
      });
      return { success: true, data: { referenceNumber: json.data.ticketNumber } };
    }

    return { success: false, error: json.error ?? 'Submission failed' };
  } catch {
    return {
      success: false,
      error: "We couldn't send your request. Please call or WhatsApp us right away.",
    };
  }
}

export async function submitContactMessage(
  payload: ContactMessagePayload,
  formStartedAt?: number
): Promise<ApiResult> {
  if (!BACKEND_ENABLED) {
    return {
      success: false,
      notConnected: true,
      error: 'Online messaging is not yet connected. Please call or email us directly.',
    };
  }

  try {
    const res = await fetch('/api/contact', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...payload, formStartedAt }),
    });

    const json = await res.json();
    if (json.ok) return { success: true };
    return { success: false, error: json.error ?? 'Submission failed' };
  } catch {
    return { success: false, error: 'Network error. Please call or email us directly.' };
  }
}

export async function getRepairStatus(
  ticket: string,
  phone: string
): Promise<ApiResult<{ status: string; timeline: unknown[] }>> {
  if (!BACKEND_ENABLED) {
    return {
      success: false,
      notConnected: true,
      error: 'Online tracking is coming soon. Please call or WhatsApp us for your repair status.',
    };
  }

  try {
    const res = await fetch('/api/track', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ticketNumber: ticket, phone }),
    });

    const json = await res.json();

    if (res.status === 429) {
      return { success: false, error: json.error ?? 'Too many attempts. Please try again later.' };
    }

    if (json.ok) {
      return {
        success: true,
        data: {
          status: json.data.publicStatus,
          timeline: json.data.timeline,
          deviceCategory: json.data.deviceCategory,
          lastUpdated: json.data.lastUpdated,
          ticketNumber: json.data.ticketNumber,
        } as unknown as { status: string; timeline: unknown[] },
      };
    }

    return { success: false, error: json.error ?? 'Not found' };
  } catch {
    return { success: false, error: 'Network error. Please call or WhatsApp us.' };
  }
}

export function trackEvent(name: string, params?: Record<string, unknown>) {
  if (typeof window !== 'undefined' && (window as unknown as { gtag?: Function }).gtag) {
    (window as unknown as { gtag: Function }).gtag('event', name, params);
  }
}